import { useState } from "react";
import { commercialYearlyHistory } from "@/mocks/commercialDetail";

interface Props {
  userId: string;
}

const MONTHS = ["1月", "2月", "3月", "4月", "5月", "6月", "7月", "8月", "9月", "10月", "11月", "12月"];
const YEAR_COLORS = ["#E8531A", "#10b981", "#f59e0b", "#6366f1"];

export default function MultiYearChart({ userId }: Props) {
  const histData = commercialYearlyHistory[userId as keyof typeof commercialYearlyHistory];
  const [selectedYears, setSelectedYears] = useState<string[]>(histData?.years ?? []);
  const [viewMode, setViewMode] = useState<"line" | "bar">("line");
  const [compareMode, setCompareMode] = useState<"monthly" | "yearly">("monthly");

  if (!histData) return null;

  const toggleYear = (year: string) => {
    setSelectedYears(prev =>
      prev.includes(year) ? (prev.length > 1 ? prev.filter(y => y !== year) : prev) : [...prev, year]
    );
  };

  const allValues = selectedYears.flatMap(y => histData.monthlyData[y as keyof typeof histData.monthlyData] ?? []);
  const maxVal = Math.max(...allValues) * 1.15;
  const minVal = 0;

  const W = 720;
  const H = 220;
  const padL = 60;
  const padR = 20;
  const padT = 20;
  const padB = 35;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;

  const toX = (i: number) => padL + (i / 11) * chartW;
  const toY = (v: number) => padT + chartH - ((v - minVal) / (maxVal - minVal)) * chartH;

  const yTicks = 5;
  const yStep = (maxVal - minVal) / yTicks;

  const latestYear = histData.years[histData.years.length - 1];
  const prevYear = histData.years[histData.years.length - 2];
  const latestData = histData.monthlyData[latestYear as keyof typeof histData.monthlyData] ?? [];
  const prevData = histData.monthlyData[prevYear as keyof typeof histData.monthlyData] ?? [];

  const latestTotal = histData.yearlyTotal[latestYear as keyof typeof histData.yearlyTotal];
  const prevTotal = histData.yearlyTotal[prevYear as keyof typeof histData.yearlyTotal];
  const yoyGrowth = (((latestTotal - prevTotal) / prevTotal) * 100).toFixed(1);

  const peakMonth = latestData.indexOf(Math.max(...latestData));
  const troughMonth = latestData.indexOf(Math.min(...latestData));

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h3 className="text-sm font-bold text-gray-900">多年用气历史对比分析</h3>
          <p className="text-xs text-gray-400 mt-0.5">年度用气量趋势 · 同比环比分析</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode("line")}
              className={`px-3 py-1 text-xs rounded-md transition-colors cursor-pointer whitespace-nowrap ${viewMode === "line" ? "bg-white text-gray-900 font-medium" : "text-gray-500"}`}
            >
              折线图
            </button>
            <button
              onClick={() => setViewMode("bar")}
              className={`px-3 py-1 text-xs rounded-md transition-colors cursor-pointer whitespace-nowrap ${viewMode === "bar" ? "bg-white text-gray-900 font-medium" : "text-gray-500"}`}
            >
              柱状图
            </button>
          </div>
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setCompareMode("monthly")}
              className={`px-3 py-1 text-xs rounded-md transition-colors cursor-pointer whitespace-nowrap ${compareMode === "monthly" ? "bg-white text-gray-900 font-medium" : "text-gray-500"}`}
            >
              月度对比
            </button>
            <button
              onClick={() => setCompareMode("yearly")}
              className={`px-3 py-1 text-xs rounded-md transition-colors cursor-pointer whitespace-nowrap ${compareMode === "yearly" ? "bg-white text-gray-900 font-medium" : "text-gray-500"}`}
            >
              年度汇总
            </button>
          </div>
        </div>
      </div>

      {/* Year Selector */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <span className="text-xs text-gray-400">选择年份：</span>
        {histData.years.map((year, idx) => (
          <button
            key={year}
            onClick={() => toggleYear(year)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full border-2 transition-all cursor-pointer whitespace-nowrap ${
              selectedYears.includes(year)
                ? "border-transparent text-white"
                : "border-gray-200 text-gray-500 bg-white"
            }`}
            style={selectedYears.includes(year) ? { backgroundColor: YEAR_COLORS[idx % YEAR_COLORS.length] } : {}}
          >
            <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: YEAR_COLORS[idx % YEAR_COLORS.length] }}></span>
            {year}年
          </button>
        ))}
      </div>

      {compareMode === "monthly" ? (
        <>
          {/* Monthly Chart */}
          <div className="overflow-x-auto">
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ minWidth: 500 }}>
              <defs>
                {selectedYears.map((year, idx) => (
                  <linearGradient key={year} id={`grad-year-${year}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={YEAR_COLORS[histData.years.indexOf(year) % YEAR_COLORS.length]} stopOpacity="0.15" />
                    <stop offset="100%" stopColor={YEAR_COLORS[histData.years.indexOf(year) % YEAR_COLORS.length]} stopOpacity="0.01" />
                  </linearGradient>
                ))}
              </defs>

              {/* Y grid */}
              {Array.from({ length: yTicks + 1 }, (_, i) => {
                const v = minVal + yStep * i;
                const y = toY(v);
                return (
                  <g key={i}>
                    <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="#f1f5f9" strokeWidth="1" />
                    <text x={padL - 5} y={y + 4} textAnchor="end" fontSize="9" fill="#94a3b8">
                      {v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v.toFixed(0)}
                    </text>
                  </g>
                );
              })}

              {/* X labels */}
              {MONTHS.map((m, i) => (
                <text key={i} x={toX(i)} y={H - 8} textAnchor="middle" fontSize="9" fill="#94a3b8">{m}</text>
              ))}

              {viewMode === "line" ? (
                selectedYears.map(year => {
                  const yIdx = histData.years.indexOf(year);
                  const color = YEAR_COLORS[yIdx % YEAR_COLORS.length];
                  const d = histData.monthlyData[year as keyof typeof histData.monthlyData] ?? [];
                  const pathD = d.map((v, i) => `${i === 0 ? "M" : "L"} ${toX(i)} ${toY(v)}`).join(" ");
                  const areaD = `${pathD} L ${toX(11)} ${padT + chartH} L ${toX(0)} ${padT + chartH} Z`;
                  return (
                    <g key={year}>
                      <path d={areaD} fill={`url(#grad-year-${year})`} />
                      <path d={pathD} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      {d.map((v, i) => (
                        <circle key={i} cx={toX(i)} cy={toY(v)} r="3" fill="white" stroke={color} strokeWidth="1.5" />
                      ))}
                    </g>
                  );
                })
              ) : (
                MONTHS.map((_, mi) => {
                  const barW = (chartW / 12) * 0.7;
                  const groupW = barW / selectedYears.length;
                  return selectedYears.map((year, yi) => {
                    const yIdx = histData.years.indexOf(year);
                    const color = YEAR_COLORS[yIdx % YEAR_COLORS.length];
                    const d = histData.monthlyData[year as keyof typeof histData.monthlyData] ?? [];
                    const v = d[mi] ?? 0;
                    const bx = toX(mi) - barW / 2 + yi * groupW;
                    const by = toY(v);
                    const bh = padT + chartH - by;
                    return (
                      <rect key={`${year}-${mi}`} x={bx} y={by} width={groupW - 1} height={bh} fill={color} rx="2" opacity="0.85" />
                    );
                  });
                })
              )}
            </svg>
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 mt-2 flex-wrap">
            {selectedYears.map(year => {
              const yIdx = histData.years.indexOf(year);
              return (
                <div key={year} className="flex items-center gap-1.5">
                  <div className="w-5 h-0.5 rounded" style={{ backgroundColor: YEAR_COLORS[yIdx % YEAR_COLORS.length] }}></div>
                  <span className="text-xs text-gray-500">{year}年</span>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        /* Yearly Summary */
        <div className="space-y-3">
          {histData.years.map((year, idx) => {
            const total = histData.yearlyTotal[year as keyof typeof histData.yearlyTotal];
            const growth = histData.yearlyGrowth[year as keyof typeof histData.yearlyGrowth];
            const maxTotal = Math.max(...Object.values(histData.yearlyTotal));
            const barPct = (total / maxTotal) * 100;
            const color = YEAR_COLORS[idx % YEAR_COLORS.length];
            return (
              <div key={year} className="flex items-center gap-4">
                <span className="text-xs font-semibold text-gray-600 w-12 flex-shrink-0">{year}年</span>
                <div className="flex-1 bg-gray-100 rounded-full h-5 relative overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                    style={{ width: `${barPct}%`, backgroundColor: color }}
                  >
                    <span className="text-xs text-white font-medium whitespace-nowrap">
                      {total.toLocaleString()} m³
                    </span>
                  </div>
                </div>
                {growth !== null ? (
                  <span className={`text-xs font-semibold w-14 text-right flex-shrink-0 ${growth > 0 ? "text-red-500" : "text-green-600"}`}>
                    {growth > 0 ? "+" : ""}{growth}%
                  </span>
                ) : (
                  <span className="text-xs text-gray-300 w-14 text-right flex-shrink-0">基准年</span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Analysis Summary */}
      <div className="mt-4 pt-4 border-t border-gray-50 grid grid-cols-4 gap-3">
        <div className="text-center">
          <p className="text-xs text-gray-400">最新年度总量</p>
          <p className="text-sm font-bold text-gray-900 mt-0.5">{latestTotal.toLocaleString()} m³</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-400">同比增长</p>
          <p className={`text-sm font-bold mt-0.5 ${Number(yoyGrowth) > 0 ? "text-red-500" : "text-green-600"}`}>
            {Number(yoyGrowth) > 0 ? "+" : ""}{yoyGrowth}%
          </p>
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-400">用气高峰月</p>
          <p className="text-sm font-bold text-[#E8531A] mt-0.5">{MONTHS[peakMonth]}</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-400">用气低谷月</p>
          <p className="text-sm font-bold text-emerald-600 mt-0.5">{MONTHS[troughMonth]}</p>
        </div>
      </div>

      {/* Month-over-month comparison table */}
      <div className="mt-4">
        <h4 className="text-xs font-semibold text-gray-700 mb-2">月度同比对比表（{latestYear} vs {prevYear}）</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-2 px-2 text-gray-400 font-medium">月份</th>
                {MONTHS.map(m => (
                  <th key={m} className="text-center py-2 px-1 text-gray-400 font-medium whitespace-nowrap">{m}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-50">
                <td className="py-2 px-2 font-semibold text-gray-700">{latestYear}年</td>
                {latestData.map((v, i) => (
                  <td key={i} className="py-2 px-1 text-center font-medium text-gray-800">
                    {v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}
                  </td>
                ))}
              </tr>
              <tr className="border-b border-gray-50">
                <td className="py-2 px-2 font-semibold text-gray-500">{prevYear}年</td>
                {prevData.map((v, i) => (
                  <td key={i} className="py-2 px-1 text-center text-gray-500">
                    {v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="py-2 px-2 font-semibold text-gray-400">同比</td>
                {latestData.map((v, i) => {
                  const p = prevData[i] ?? 1;
                  const rate = (((v - p) / p) * 100).toFixed(0);
                  return (
                    <td key={i} className={`py-2 px-1 text-center font-medium ${Number(rate) > 0 ? "text-red-500" : "text-green-600"}`}>
                      {Number(rate) > 0 ? "+" : ""}{rate}%
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
